// The example machine-readable zones a country page publishes.
//
// One invented holder appears in every example this product ships — the
// specimen persona, whose canonical record lives in
// packages/engine-adapter/src/__fixtures__/specimen-persona.json and whose
// documents are issued by Greece there. A country page re-issues her documents
// under that page's own state, so a reader meets the same person in the docs,
// in the recognition fixture and here rather than three invented people.
//
// Nothing in this file is real. `paradeigma` is the Greek word for *example*;
// the numbers, the dates and the zones are invented, and the check digits are
// computed from the invented characters by the generator rather than typed in.

import { type Country, zoneCodeOf } from "./countries";
import {
  generateTd1,
  generateTd2,
  generateTd3,
  TD1_LINE_LENGTH,
  TD2_LINE_LENGTH,
  TD3_LINE_LENGTH,
} from "./mrz";

// The holder, fixed. Only the issuing state moves from page to page.
//
// Exported because a page that prints an example RESPONSE needs the same
// invented person the example ZONE is built around; two invented holders on one
// page reads as a mistake, and is one.
export const SPECIMEN_HOLDER = {
  surname: "PARADEIGMA",
  givenNames: "ELENI SOFIA",
  dateOfBirth: "940308",
  sex: "F",
} as const;

/** The nationality the specimen holds, wherever her document was issued. */
export const SPECIMEN_NATIONALITY = "GRC";

/**
 * The specimen's passport number. Exported because the check-digit explainer
 * works the arithmetic through on exactly this field, and a page that retyped
 * it would be showing a reader a sum over a different number than the zone
 * beside it carries.
 */
export const SPECIMEN_DOCUMENT_NUMBER = "AM7304518";

export type ZoneFormat = "TD1" | "TD2" | "TD3";

export interface CountryZone {
  readonly format: ZoneFormat;
  /** What the document is, in the reader's words. */
  readonly document: string;
  /** How long each line of this format is. */
  readonly lineLength: number;
  readonly lines: readonly string[];
  /** The nationality printed in this zone, which is not always the issuer. */
  readonly nationality: string;
  /** Why this example is shaped the way it is, in one sentence. */
  readonly note: string;
}

/**
 * The TD3 zone of a passport issued by one state to its own national. Two lines
 * of 44.
 *
 * The issuing state is a parameter because a country page re-issues the
 * specimen's passport under that page's own state; every other value is the
 * specimen's and never moves.
 */
function passportIssuedBy(alpha3: string) {
  return generateTd3({
    ...SPECIMEN_HOLDER,
    documentType: "P",
    issuingCountry: alpha3,
    // A passport is issued by the state whose nationality it certifies, so
    // these two codes agree here and only here.
    nationality: alpha3,
    documentNumber: SPECIMEN_DOCUMENT_NUMBER,
    expiryDate: "320310",
    personalNumber: "PN48291630",
  });
}

/** The TD3 zone of a passport this country issued. Two lines of 44. */
export function exampleMrz(country: Country) {
  return passportIssuedBy(zoneCodeOf(country));
}

/**
 * The specimen's three documents, issued by one state: her passport, a travel
 * document and an identity card or residence permit.
 *
 * The invented numbers and dates are written here once. Two pages need the same
 * three zones — a country page, which re-issues them under its own state, and
 * the format explainer, which is about the layouts themselves — and a second
 * copy of these values would be the one a reader checking our arithmetic finds
 * disagreeing with the first.
 */
function specimenDocumentsIssuedBy(alpha3: string) {
  return {
    passport: passportIssuedBy(alpha3),
    travel: generateTd2({
      ...SPECIMEN_HOLDER,
      documentType: "I<",
      issuingCountry: alpha3,
      nationality: SPECIMEN_NATIONALITY,
      documentNumber: "TR6185042",
      expiryDate: "291104",
    }),
    permit: generateTd1({
      ...SPECIMEN_HOLDER,
      documentType: "ID",
      issuingCountry: alpha3,
      nationality: SPECIMEN_NATIONALITY,
      documentNumber: "AK472913",
      expiryDate: "340520",
      optionalData: "PN48291630",
    }),
  };
}

/** One zone layout, with the specimen's own document in it. */
export interface FormatZone {
  readonly format: ZoneFormat;
  readonly lineLength: number;
  readonly lines: readonly string[];
}

/**
 * The three layouts with the specimen's own state as the issuer, for a page
 * that is about the formats rather than about any one country.
 */
export function specimenFormatZones(): readonly FormatZone[] {
  const { passport, travel, permit } = specimenDocumentsIssuedBy(SPECIMEN_NATIONALITY);
  return [
    { format: "TD3", lineLength: TD3_LINE_LENGTH, lines: [passport.line1, passport.line2] },
    { format: "TD2", lineLength: TD2_LINE_LENGTH, lines: [travel.line1, travel.line2] },
    {
      format: "TD1",
      lineLength: TD1_LINE_LENGTH,
      lines: [permit.line1, permit.line2, permit.line3],
    },
  ];
}

/**
 * The specimen's own passport, for a page that is about the product rather than
 * about one issuing state. Same invented holder as everywhere else, so a reader
 * who has met her on a country page or in the documentation meets her again.
 */
export function specimenPassportMrz() {
  return passportIssuedBy(SPECIMEN_NATIONALITY);
}

/**
 * The three zone layouts with this country as the issuing state, in the order
 * a reader meets them: the passport first, because that is what the page is
 * about.
 *
 * The two non-passport examples keep the specimen's own nationality rather than
 * taking the issuer's. That is the case the formats exist for — a state issues
 * a TD2 travel document or a TD1 permit to somebody who is not its national —
 * and it is the one place a reader can see that `issuing_state` and
 * `nationality` are two fields and not one.
 */
export function countryZones(country: Country): readonly CountryZone[] {
  const zone = zoneCodeOf(country);
  const { passport, travel, permit } = specimenDocumentsIssuedBy(zone);

  return [
    {
      format: "TD3",
      document: `${country.name} passport`,
      lineLength: TD3_LINE_LENGTH,
      lines: [passport.line1, passport.line2],
      nationality: zone,
      note:
        `Issuing state and nationality are both ${zone}: a passport certifies ` +
        "the nationality of the state that issued it.",
    },
    {
      format: "TD2",
      document: `Travel document issued by ${country.name}`,
      lineLength: TD2_LINE_LENGTH,
      lines: [travel.line1, travel.line2],
      nationality: SPECIMEN_NATIONALITY,
      note:
        `Issued by ${zone} to a ${SPECIMEN_NATIONALITY} national, so the two codes ` +
        "differ. The name has 31 characters here instead of the passport's 39.",
    },
    {
      format: "TD1",
      document: `Identity card or residence permit issued by ${country.name}`,
      lineLength: TD1_LINE_LENGTH,
      lines: [permit.line1, permit.line2, permit.line3],
      nationality: SPECIMEN_NATIONALITY,
      // No markdown in these notes: they are rendered as text, so a backtick
      // would reach the reader as a backtick.
      note:
        "Three lines of 30, with the name on a line of its own. The document code is ID – " +
        "9303 part 5 fixes the first character as A, C or I and leaves the second to the " +
        "issuing state.",
    },
  ];
}
